import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Badge, Spinner, Alert, Button, Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, FormGroup } from 'reactstrap';
import { H5, H6 } from '../../../AbstractElements';
import { loadingAllAdmin, register, editAdmin, deleteAdmin, isAuthenticated } from '../../../api/admin';
import { loadingAllRole } from '../../../api/role';
import { Users, Plus, Edit, Trash2, RefreshCw } from 'react-feather';

const AdminManagement = () => {
  const [adminData, setAdminData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    role_code: ''
  });

  useEffect(() => {
    fetchAdminData();
    fetchRoleData();
  }, []);

  const fetchAdminData = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!isAuthenticated()) {
        throw new Error('Chưa đăng nhập');
      }

      const admins = await loadingAllAdmin();
      setAdminData(admins);
    } catch (err) {
      console.error('❌ Error loading admin data:', err);
      setError(err.message || 'Không thể tải dữ liệu admin');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleData = async () => {
    try {
      const roles = await loadingAllRole();
      setRoleData(roles);
      
      // Set default role_code nếu có roles
      if (roles.length > 0 && !formData.role_code) {
        setFormData(prev => ({
          ...prev,
          role_code: roles[0].code
        }));
      }
    } catch (err) {
      console.error('❌ Error loading role data:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddAdmin = async () => {
    try {
      if (!formData.name || !formData.username || !formData.password) {
        alert('Vui lòng điền đầy đủ thông tin');
        return;
      }

      await register(formData.name, formData.username, formData.password, formData.role_code);
      setModal(false);
      setFormData({ name: '', username: '', password: '', role_code: 'R2' });
      await fetchAdminData();
      alert('Thêm admin thành công!');
    } catch (err) {
      console.error('❌ Error adding admin:', err);
      alert('Lỗi khi thêm admin: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEditAdmin = async () => {
    try {
      if (!selectedAdmin || !formData.name || !formData.username) {
        alert('Vui lòng điền đầy đủ thông tin');
        return;
      }

      const updateData = {
        name: formData.name,
        username: formData.username,
        role_code: formData.role_code
      };

      // Chỉ thêm password nếu có
      if (formData.password) {
        updateData.password = formData.password;
      }

      await editAdmin(selectedAdmin.id, updateData);
      setEditModal(false);
      setSelectedAdmin(null);
      setFormData({ name: '', username: '', password: '', role_code: 'R2' });
      await fetchAdminData();
      alert('Cập nhật admin thành công!');
    } catch (err) {
      console.error('❌ Error editing admin:', err);
      alert('Lỗi khi cập nhật admin: ' + (err.message || 'Unknown error'));
    }
  };

  const handleDeleteAdmin = async () => {
    try {
      if (!selectedAdmin) {
        alert('Không tìm thấy admin để xóa');
        return;
      }

      await deleteAdmin(selectedAdmin.id);
      setDeleteModal(false);
      setSelectedAdmin(null);
      await fetchAdminData();
      alert('Xóa admin thành công!');
    } catch (err) {
      console.error('❌ Error deleting admin:', err);
      alert('Lỗi khi xóa admin: ' + (err.message || 'Unknown error'));
    }
  };

  const openEditModal = (admin) => {
    setSelectedAdmin(admin);
    setFormData({
      name: admin.name || '',
      username: admin.username || '',
      password: '',
      role_code: admin.role_code || 'R2'
    });
    setEditModal(true);
  };

  const openDeleteModal = (admin) => {
    setSelectedAdmin(admin);
    setDeleteModal(true);
  };

  const toggleModal = () => {
    setModal(!modal);
    if (!modal) {
      setFormData({ 
        name: '', 
        username: '', 
        password: '', 
        role_code: roleData.length > 0 ? roleData[0].code : '' 
      });
    }
  };

  const toggleEditModal = () => {
    setEditModal(!editModal);
    if (!editModal) {
      setSelectedAdmin(null);
      setFormData({ 
        name: '', 
        username: '', 
        password: '', 
        role_code: roleData.length > 0 ? roleData[0].code : '' 
      });
    }
  };

  const toggleDeleteModal = () => {
    setDeleteModal(!deleteModal);
    if (!deleteModal) {
      setSelectedAdmin(null);
    }
  };

  // Helper function để lấy tên role từ role_code
  const getRoleName = (roleCode) => {
    const role = roleData.find(r => r.code === roleCode);
    return role ? role.value : roleCode;
  };

  // Helper function để lấy màu badge cho role
  const getRoleBadgeColor = (roleCode) => {
    const colorMap = {
      'R1': 'danger',
      'R2': 'warning',
      'R3': 'success',
      'R4': 'info',
    };
    return colorMap[roleCode] || 'secondary';
  };

  if (loading) {
    return (
      <Col xxl="12" className="box-col-12">
        <Card>
          <CardBody className="d-flex justify-content-center align-items-center" style={{ height: '200px' }}>
            <div className="text-center">
              <Spinner color="primary" />
              <p className="mt-2 mb-0">Đang tải dữ liệu admin...</p>
            </div>
          </CardBody>
        </Card>
      </Col>
    );
  }

  if (error) {
    return (
      <Col xxl="12" className="box-col-12">
        <Card>
          <CardBody>
            <Alert color="danger">
              <strong>Lỗi:</strong> {error}
            </Alert>
            <Button color="primary" size="sm" onClick={fetchAdminData}>
              Thử lại
            </Button>
          </CardBody>
        </Card>
      </Col>
    );
  }

  return (
    <Col xxl="12" className="box-col-12">
      <Card>
        <CardHeader className="card-no-border d-flex justify-content-between align-items-center">
          <H5>Quản Lý Admin</H5>
          <div className="d-flex gap-2">
            <Button color="outline-primary" size="sm" onClick={fetchAdminData}>
              <RefreshCw size={16} className="me-1" />
              Làm mới
            </Button>
            <Button color="primary" size="sm" onClick={toggleModal}>
              <Plus size={16} className="me-1" />
              Thêm Admin
            </Button>
          </div>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Tên</th>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Trạng Thái</th>
                  <th>Ngày Tạo</th>
                  <th>Hành Động</th>
                </tr>
              </thead>
              <tbody>
                {adminData.map((admin) => (
                  <tr key={admin.id}>
                    <td>{admin.id}</td>
                    <td>{admin.name}</td>
                    <td>{admin.username}</td>
                    <td>
                      <Badge color={getRoleBadgeColor(admin.role_code)}>
                        {getRoleName(admin.role_code)}
                      </Badge>
                    </td>
                    <td>
                      <Badge color={admin.isActive !== false ? 'success' : 'danger'}>
                        {admin.isActive !== false ? 'Hoạt động' : 'Không hoạt động'}
                      </Badge>
                    </td>
                    <td>
                      {admin.createdAt ? new Date(admin.createdAt).toLocaleDateString('vi-VN') : 'N/A'}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button 
                          color="outline-warning" 
                          size="sm" 
                          onClick={() => openEditModal(admin)}
                        >
                          <Edit size={14} />
                        </Button>
                        <Button 
                          color="outline-danger" 
                          size="sm" 
                          onClick={() => openDeleteModal(admin)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>

      {/* Modal Thêm Admin */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <ModalHeader toggle={toggleModal}>Thêm Admin Mới</ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md="6">
              <FormGroup>
                <Label>Tên Admin *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Nhập tên admin"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Username *</Label>
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Nhập username"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Password *</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Nhập password"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Role *</Label>
                <Input
                  type="select"
                  name="role_code"
                  value={formData.role_code}
                  onChange={handleInputChange}
                >
                  <option value="">-- Chọn Role --</option>
                  {roleData.map((role) => (
                    <option key={role.id} value={role.code}>
                      {role.code} - {role.value}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>Hủy</Button>
          <Button color="primary" onClick={handleAddAdmin}>Thêm Admin</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Sửa Admin */}
      <Modal isOpen={editModal} toggle={toggleEditModal} size="lg">
        <ModalHeader toggle={toggleEditModal}>Sửa Admin</ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col md="6">
              <FormGroup>
                <Label>Tên Admin *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Nhập tên admin"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Username *</Label>
                <Input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  placeholder="Nhập username"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Password (Để trống nếu không đổi)</Label>
                <Input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Nhập password mới"
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label>Role *</Label>
                <Input
                  type="select"
                  name="role_code"
                  value={formData.role_code}
                  onChange={handleInputChange}
                >
                  <option value="">-- Chọn Role --</option>
                  {roleData.map((role) => (
                    <option key={role.id} value={role.code}>
                      {role.code} - {role.value}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditModal}>Hủy</Button>
          <Button color="warning" onClick={handleEditAdmin}>Cập Nhật</Button>
        </ModalFooter>
      </Modal>

      {/* Modal Xóa Admin */}
      <Modal isOpen={deleteModal} toggle={toggleDeleteModal}>
        <ModalHeader toggle={toggleDeleteModal}>Xác Nhận Xóa</ModalHeader>
        <ModalBody>
          <p>Bạn có chắc chắn muốn xóa admin <strong>{selectedAdmin?.name}</strong>?</p>
          <Alert color="warning">
            <strong>Cảnh báo:</strong> Hành động này không thể hoàn tác!
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleDeleteModal}>Hủy</Button>
          <Button color="danger" onClick={handleDeleteAdmin}>Xóa</Button>
        </ModalFooter>
      </Modal>
    </Col>
  );
};

export default AdminManagement;
